/**
 * Gated download page for the CherryClipz Premiere Pro panel (.zxp).
 *
 * The page is public so anyone can read what the panel is and what they'll need. The FILE
 * is not: the button asks the backend for a signed, short-lived URL using the user's token,
 * so you must be signed in to get it, every download is recorded, and a link pasted
 * somewhere public is dead within minutes.
 *
 * We don't try to stop the .zxp being passed around afterwards — that isn't possible with a
 * plain file, and it doesn't matter: the panel is inert without a CherryClipz login and
 * every transcribe is metered against that account's plan.
 *
 * Styled to match the flagship (R) and plugin (S) pages via the shared `.ccdl`-scoped brand
 * CSS, so it reads as part of the product rather than a bare utility screen.
 */
import React, { useContext, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { userContext } from "../../stores/users/userContext";
import { messageContext } from "../../stores/messages/messageContext";
import { hostApi, pluginDownloadLinkUrl, EMO_EMAIL } from "../constants";
import { ROUTES } from "../../constants/routes";
import { POST_LOGIN_INTENT_KEY } from "../../constants/postLoginIntent";
import apiRequest from "../../utils/apiClient";
import { brandPageCss } from "../brand/brandPageCss";
import {
  BrandNav,
  BrandFooter,
  DownloadSvg,
  useBrandTheme,
} from "../brand/BrandChrome";
import {
  PLUGIN_EYEBROW,
  PLUGIN_DOWNLOAD_TITLE,
  PLUGIN_DOWNLOAD_SUBTITLE,
  PLUGIN_DOWNLOAD_CTA,
  PLUGIN_DOWNLOAD_FINE,
  PLUGIN_SIGN_IN_CTA,
  PLUGIN_SIGN_IN_FINE,
  PLUGIN_SIGN_IN_SECONDARY,
  PLUGIN_SETUP_EYEBROW,
  PLUGIN_SETUP_HEADING,
  PLUGIN_SETUP_STEPS,
  PLUGIN_INSTALLER_PRIMARY,
  PLUGIN_INSTALLER_ALT,
  PLUGIN_INSTALLER_ALT_PREFIX,
  PLUGIN_USE_EYEBROW,
  PLUGIN_USE_HEADING,
  PLUGIN_USE_STEPS,
  PLUGIN_UNVERIFIED_NOTE,
  PLUGIN_HELP_PREFIX,
  PLUGIN_WINDOWS_NOTE,
  PLUGIN_GENERIC_ERROR,
} from "./pluginConstants";

const SCOPE = "ccdl";

const PluginDownloadPage: React.FC = () => {
  const navigate = useNavigate();
  const userStore = useContext(userContext);
  const messageStore = useContext(messageContext);
  const [theme, toggleTheme] = useBrandTheme();
  const [loading, setLoading] = useState(false);

  // Two states (since 2026-10-25 the panel is free to download on every plan): signed out →
  // sign in, and come straight back here; signed in → download. What the panel can transcribe
  // is capped by the plan's hours on the server, like everything else.
  const isAuthed = userStore.isUserAuth();
  const signInThenBack = () => {
    try {
      sessionStorage.setItem(POST_LOGIN_INTENT_KEY, ROUTES.PLUGIN_DOWNLOAD);
    } catch (e) {
      /* ignore */
    }
    navigate(ROUTES.LANDING);
  };

  const handleDownload = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await apiRequest(pluginDownloadLinkUrl, { method: "GET" });
      const data = await response.json();

      if (!response.ok || !data.path) {
        // 503 = no build published yet; 401 = signed out. Both send back usable wording.
        throw new Error(data.error || PLUGIN_GENERIC_ERROR);
      }

      // Hand the signed link to the browser so it's a real download, with a real filename.
      window.location.href = `${hostApi}${data.path}`;
    } catch (err: unknown) {
      messageStore.setMessage(
        err instanceof Error ? err.message : PLUGIN_GENERIC_ERROR,
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={SCOPE} data-theme={theme}>
      <style>{brandPageCss(SCOPE) + PAGE_CSS}</style>

      <BrandNav theme={theme} onToggleTheme={toggleTheme}>
        <button className="linkbtn hide-sm" onClick={() => navigate(ROUTES.MY_LIBRARY)}>
          My library
        </button>
        <button className="linkbtn hide-sm" onClick={() => navigate(ROUTES.PRICING)}>
          Pricing
        </button>
        <button className="linkbtn hide-sm" onClick={() => navigate(ROUTES.CONTACT)}>
          Help
        </button>
      </BrandNav>

      <div className="wrap hero">
        <span className="eyebrow">{PLUGIN_EYEBROW}</span>
        <h1>{PLUGIN_DOWNLOAD_TITLE}</h1>
        <p className="sub">{PLUGIN_DOWNLOAD_SUBTITLE}</p>

        <div className="cta">
          {isAuthed ? (
            <button
              className="btn btn-cherry btn-lg"
              onClick={handleDownload}
              disabled={loading}
            >
              <DownloadSvg />
              {loading ? "Starting…" : PLUGIN_DOWNLOAD_CTA}
            </button>
          ) : (
            <>
              <button className="btn btn-cherry btn-lg" onClick={signInThenBack}>
                {PLUGIN_SIGN_IN_CTA}
              </button>
              <button
                className="btn btn-ghost btn-lg"
                onClick={() => navigate(ROUTES.PRICING)}
              >
                {PLUGIN_SIGN_IN_SECONDARY}
              </button>
            </>
          )}
        </div>
        <p className="fine">{isAuthed ? PLUGIN_DOWNLOAD_FINE : PLUGIN_SIGN_IN_FINE}</p>
        <p className="fine">
          {PLUGIN_WINDOWS_NOTE} <a href={`mailto:${EMO_EMAIL}`}>{EMO_EMAIL}</a>
        </p>
      </div>

      {/* Numbered because this genuinely is a sequence — you can't do 3 before 1. */}
      <section className="wrap">
        <div className="sec-head">
          <span className="eyebrow">{PLUGIN_SETUP_EYEBROW}</span>
          <h2>{PLUGIN_SETUP_HEADING}</h2>
        </div>

        <ol className="steps">
          {PLUGIN_SETUP_STEPS.map((step, i) => (
            <li className="step" key={step.title}>
              <span className="k mono">{String(i + 1).padStart(2, "0")}</span>
              <div className="step-body">
                <h3>{step.title}</h3>
                <p>{step.body}</p>
                {step.title === PLUGIN_SETUP_STEPS[0].title && (
                  <p className="installer">
                    <a
                      href={PLUGIN_INSTALLER_PRIMARY.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {PLUGIN_INSTALLER_PRIMARY.name} ↗
                    </a>
                    <span className="alt">
                      {" "}
                      {PLUGIN_INSTALLER_ALT_PREFIX}{" "}
                      <a
                        href={PLUGIN_INSTALLER_ALT.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {PLUGIN_INSTALLER_ALT.name} ↗
                      </a>
                    </span>
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>

        <p className="note">{PLUGIN_UNVERIFIED_NOTE}</p>
      </section>

      <section className="wrap">
        <div className="sec-head">
          <span className="eyebrow">{PLUGIN_USE_EYEBROW}</span>
          <h2>{PLUGIN_USE_HEADING}</h2>
        </div>
        <ul className="uses">
          {PLUGIN_USE_STEPS.map((use) => (
            <li key={use}>{use}</li>
          ))}
        </ul>
      </section>

      <BrandFooter>
        <span>
          {PLUGIN_HELP_PREFIX} <a href={`mailto:${EMO_EMAIL}`}>{EMO_EMAIL}</a>
        </span>
      </BrandFooter>
    </div>
  );
};

const PAGE_CSS = `
.ccdl .hero .mac{display:inline-flex;align-items:center;gap:7px}
.ccdl .steps{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:2px;counter-reset:none}
.ccdl .step{display:flex;gap:20px;padding:22px 0;border-top:1px solid var(--line)}
.ccdl .step:first-child{border-top:none;padding-top:0}
.ccdl .step .k{flex:none;font-size:13px;font-weight:700;letter-spacing:.1em;color:var(--cherry);padding-top:3px;width:26px}
.ccdl .step-body{min-width:0}
.ccdl .step h3{font-size:18px;margin-bottom:6px}
.ccdl .step p{margin:0;color:var(--muted);font-size:14.5px}
.ccdl .step .installer{margin-top:10px;font-size:14.5px}
.ccdl .step .installer .alt{color:var(--faint);font-size:13px}
.ccdl .uses{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:14px}
.ccdl .uses li{position:relative;padding-left:26px;color:var(--muted);font-size:15px}
.ccdl .uses li::before{content:"";position:absolute;left:6px;top:9px;width:7px;height:7px;border-radius:50%;background:var(--cherry)}
@media (max-width:560px){ .ccdl .step{gap:14px} }
`;

export default observer(PluginDownloadPage);
